import { Gear } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useCallback, useEffect, useId, useState } from "react";

import { useLocale } from "~/hooks/locale";
import type { messages } from "~/i18n/messages";
import { trackEvent } from "~/utils/analytics";
import { TIME_LIMIT_MIN } from "~/utils/const";

import type { GameWord } from "../Game";
import { useGame } from "../GameContext";
import { GameLabel } from "../GameLabel";
import { LanguageSelect } from "./LanguageSelect";
import { PreGameIdContext } from "./PreGameIdContext";
import { PreGameSettings } from "./PreGameSettings";

export const PreGame: React.FC<{
  title: React.ReactElement;
  children: React.ReactNode;
  queryFn?: () => Promise<GameWord[]>;
  minTimeLimit?: number;
  isConfigurable?: boolean;
  label?: keyof typeof messages;
  eventName: string;
  eventData?: Record<string, string>;
}> = ({
  title,
  children,
  queryFn,
  minTimeLimit = TIME_LIMIT_MIN,
  isConfigurable = true,
  label = "select_game_mode",
  eventName,
  eventData,
}) => {
  const { t } = useLocale();
  const { setGame, header } = useGame();

  const [isReady, setIsReady] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const startGame = useCallback(() => {
    setIsReady(true);
  }, []);

  useEffect(() => {
    // press enter key to start the game
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        // prevent this from firing if an input or textarea is focused
        event.target instanceof HTMLInputElement === false &&
        event.target instanceof HTMLTextAreaElement === false &&
        event.key === "Enter" &&
        queryFn
      ) {
        startGame();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [startGame, queryFn]);

  const id = useId();
  const { data, isLoading, isSuccess } = useQuery({
    staleTime: Infinity,
    enabled: isReady,
    queryKey: ["words", id],
    queryFn,
  });

  useEffect(() => {
    if (data) {
      trackEvent(eventName, eventData);
      setIsReady(false);
      setGame((prev) => ({
        ...prev,
        time: 0,
        answers: [],
        words: data,
      }));
    }
  }, [data, setGame, eventName, eventData]);

  return (
    <div className="absolute inset-0 flex flex-col">
      {header}
      <div className="relative z-0 flex grow flex-col divide-y">
        <div className="sm:pad">
          <div className="relative px-4 py-pad wrap sm:border-x md:px-6">
            {title}
            <GameLabel position="bottom-left">
              {t(isSettingsOpen ? "game_settings" : label)}
            </GameLabel>
          </div>
        </div>
        <div className="flex h-0 grow sm:pad">
          <div className="relative overflow-auto p-4 transition-colors wrap sm:border-x md:p-6">
            <AnimatePresence initial={false} mode="popLayout">
              {isSettingsOpen ? (
                <PreGameIdContext value={id}>
                  <PreGameSettings
                    key="settings"
                    minTimeLimit={minTimeLimit}
                    onClose={() => {
                      setIsSettingsOpen(false);
                      trackEvent("game_settings_closed");
                    }}
                  />
                </PreGameIdContext>
              ) : (
                <motion.div
                  key="modes"
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
                >
                  {children}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className="relative bg-base-100 sm:pad">
          <div className="flex justify-between p-4 wrap sm:border-x md:px-6">
            <div className="flex gap-3 lg:gap-4">
              <LanguageSelect />
              {isConfigurable && (
                <button
                  className={clsx(
                    "btn btn-outline max-sm:btn-square lg:btn-lg",
                    isSettingsOpen && "btn-active",
                  )}
                  onClick={() => {
                    setIsSettingsOpen((prev) => !prev);
                    trackEvent(
                      `game_settings_${isSettingsOpen ? "closed" : "opened"}`,
                    );
                  }}
                >
                  <Gear className="size-5 lg:size-6" weight="fill" />
                  <span className="max-sm:hidden">{t("settings")}</span>
                </button>
              )}
            </div>
            <button
              className="btn btn-secondary lg:btn-lg"
              disabled={!queryFn || isLoading || isSuccess}
              onClick={startGame}
            >
              <span className="kbd kbd-xs lg:kbd-sm max-md:hidden">enter</span>
              <span className="max-sm:hidden">{t("start_game")}</span>
              <span aria-hidden className="sm:hidden">
                {t("start")}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
